import { PanTool, RectangleOutlined } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  Dropdown,
  IconButton,
  Menu,
  MenuButton,
  Stack,
  ToggleButtonGroup,
} from "@mui/joy";
import { useEffect, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import { useRoutesUiStore } from "@/components/RoutesProvider";

import { useCurrentVenueQuery } from "../../hooks/venues";
import { useDisplayedMapsWithDefault } from "../RoutesProvider/RoutesProvider";

export default function MapConfigDropdown() {
  const { data: venue } = useCurrentVenueQuery();
  const maps = useMemo(() => (venue?.maps || []).filter((map) => map.available), [venue]);

  useEffect(() => {
    if (navigator.maxTouchPoints > 0) {
      setMapMode("pan");
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, []);

  const { maps: displayedMaps } = useDisplayedMapsWithDefault();

  const displayedMapUuids = useMemo(
    () => new Set(displayedMaps.map((map) => map.uuid)),
    [displayedMaps],
  );

  const { setDisplayedMapUuids, mapMode, setMapMode } = useRoutesUiStore(
    useShallow((state) => ({
      setDisplayedMapUuids: state.setDisplayedMapUuids,
      mapMode: state.mapMode,
      setMapMode: state.setMapMode,
    })),
  );

  return (
    <Stack
      direction="row"
      gap={0.5}
      sx={{
        position: "absolute",
        top: 8,
        right: 8,
        zIndex: 1000,
        ".MuiIconButton-root, .MuiMenuButton-root": {
          backgroundColor: "var(--joy-palette-neutral-900)",
          border: "1px solid",
          borderColor: "var(--joy-palette-neutral-800)!important",
          "&:hover, &[aria-pressed=true]": {
            backgroundColor: "var(--joy-palette-neutral-800)",
          },
        },
      }}
    >
      <ToggleButtonGroup
        color="neutral"
        variant="solid"
        size="sm"
        value={mapMode}
        onChange={(_e, v) => v && setMapMode(v)}
      >
        <IconButton value="select">
          <RectangleOutlined />
        </IconButton>
        <IconButton value="pan">
          <PanTool />
        </IconButton>
      </ToggleButtonGroup>
      <Dropdown>
        <MenuButton variant="solid" color="neutral" size="sm">
          Map Config
        </MenuButton>
        <Menu
          placement="bottom-end"
          sx={{
            backgroundColor: "var(--joy-palette-neutral-900)",
            borderColor: "var(--joy-palette-neutral-800)",
          }}
        >
          {maps.map((map) => (
            <Box key={map.uuid} sx={{ overflow: "hidden" }}>
              <Checkbox
                checked={displayedMapUuids.has(map.uuid)}
                size="sm"
                onChange={(e) => {
                  if (e.target.checked) {
                    setDisplayedMapUuids([...displayedMapUuids, map.uuid]);
                  } else {
                    setDisplayedMapUuids(
                      [...displayedMapUuids].filter((uuid) => uuid !== map.uuid),
                    );
                  }
                }}
                label={map.name}
                slotProps={{
                  root: {
                    sx: {
                      paddingX: 1,
                      paddingY: 0.5,
                      display: "flex",
                    },
                  },
                  label: {
                    sx: {
                      color: "var(--joy-palette-neutral-200)",
                    },
                  },
                  checkbox: {
                    sx: {
                      backgroundColor: "var(--joy-palette-neutral-800)",
                      "&:hover": {
                        backgroundColor: "var(--joy-palette-neutral-700)",
                        borderColor: "var(--joy-palette-neutral-700)",
                      },
                      borderColor: "var(--joy-palette-neutral-700)",
                    },
                  },
                }}
              />
            </Box>
          ))}
        </Menu>
      </Dropdown>
    </Stack>
  );
}
